import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { assignmentAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiClipboard, FiClock, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { format } from 'date-fns';

const Assignments = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchAssignments();
  }, [selectedClass, selectedStatus]);

  const fetchInitialData = async () => {
    try {
      const classesRes = await masterAPI.getClasses();
      if (classesRes.data.success) {
        setClasses(classesRes.data.data);
      }
    } catch (error) {
      toast.error('Error loading data');
      console.error(error);
    }
  };

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedClass) params.class_id = selectedClass;
      if (selectedStatus) params.status = selectedStatus;

      const response = await assignmentAPI.getAssignments(params);
      if (response.data.success) {
        setAssignments(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching assignments');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    try {
      const response = await assignmentAPI.deleteAssignment(id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchAssignments();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting assignment');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-800',
      published: 'bg-green-100 text-green-800',
      closed: 'bg-red-100 text-red-800'
    };
    return badges[status] || badges.draft;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'published':
        return <FiCheckCircle className="text-green-600" />;
      case 'closed':
        return <FiAlertCircle className="text-red-600" />;
      default:
        return <FiClock className="text-gray-600" />;
    }
  };

  const filteredAssignments = assignments.filter(assignment =>
    assignment.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    assignment.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Assignments & Homework</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Manage student assignments and track submissions</p>
        </div>
        <button
          onClick={() => navigate('/assignments/create')}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <FiPlus /> Create Assignment
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              placeholder="Search assignments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Class</label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
            >
              <option value="">All Classes</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>{cls.class}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchAssignments}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Assignments List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading assignments...</p>
          </div>
        ) : filteredAssignments.length === 0 ? (
          <div className="text-center py-12">
            <FiClipboard size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Assignments Found</h3>
            <p className="text-gray-600 mb-6">
              {searchQuery ? 'No assignments match your search' : 'Get started by creating your first assignment'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => navigate('/assignments/create')}
                className="inline-flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <FiPlus /> Create First Assignment
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            {filteredAssignments.map(assignment => (
              <div key={assignment.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(assignment.status)}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(assignment.status)}`}>
                      {assignment.status}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        console.log('Navigating to:', `/assignments/${assignment.id}/submissions`);
                        console.log('Assignment ID:', assignment.id);
                        navigate(`/assignments/${assignment.id}/submissions`);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="View Submissions"
                    >
                      <FiEye size={16} />
                    </button>
                    <button
                      onClick={() => navigate(`/assignments/edit/${assignment.id}`)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                      title="Edit"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(assignment.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2">{assignment.title}</h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{assignment.description}</p>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Class:</span>
                    <span className="font-medium text-gray-900">{assignment.class_name}</span>
                  </div>
                  {assignment.batch_name && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Batch:</span>
                      <span className="font-medium text-gray-900">{assignment.batch_name}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Due Date:</span>
                    <span className="font-medium text-gray-900">
                      {format(new Date(assignment.due_date), 'MMM dd, yyyy')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Marks:</span>
                    <span className="font-medium text-gray-900">{assignment.total_marks}</span>
                  </div>
                  {assignment.passing_marks && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Passing Marks:</span>
                      <span className="font-medium text-gray-900">{assignment.passing_marks}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Submissions:</span>
                    <span className="font-medium text-blue-600">{assignment.submission_count || 0}</span>
                  </div>
                </div>

                {assignment.attachment_path && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <a
                      href={assignment.attachment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View Attachment
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Assignments;
